import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import AdminLayout from "./layouts/AdminLayout";
import {
  Bell,
  CheckCircle,
  RefreshCw,
  AlertTriangle,
  Info,
  Mail,
  UserPlus,
  FileText,
  MessageSquare,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Settings,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Clock,
  AlertCircle,
  CheckCircle2,
  Archive,
  Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const NotificationIcon = ({ type, priority = 'normal' }) => {
  const icons = {
    job: <FileText className="w-5 h-5 text-blue-500" />,
    application: <UserPlus className="w-5 h-5 text-green-500" />,
    message: <MessageSquare className="w-5 h-5 text-purple-500" />,
    system: <Info className="w-5 h-5 text-gray-500" />,
    alert: <AlertTriangle className="w-5 h-5 text-red-500" />,
    email: <Mail className="w-5 h-5 text-yellow-500" />,
    blog_draft: <FileText className="w-5 h-5 text-indigo-500" />,
    new_application: <UserPlus className="w-5 h-5 text-emerald-500" />,
    default: <Bell className="w-5 h-5 text-blue-500" />
  };

  const baseIcon = icons[type] || icons.default;
  
  if (priority === 'high') {
    return (
      <div className="relative">
        {baseIcon}
        <AlertCircle className="absolute -top-1 -right-1 w-3 h-3 text-red-500" />
      </div>
    );
  }
  
  if (priority === 'urgent') {
    return (
      <div className="relative animate-pulse">
        {baseIcon}
        <Star className="absolute -top-1 -right-1 w-3 h-3 text-red-600 fill-current" />
      </div>
    );
  }

  return baseIcon;
};

const PriorityBadge = ({ priority }) => {
  const badges = {
    urgent: { bg: 'bg-red-100', text: 'text-red-800', label: 'Urgent' },
    high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
    normal: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Normal' },
    low: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Low' }
  };

  const badge = badges[priority] || badges.normal;
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
};

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [sortBy, setSortBy] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);

  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const userId = "admin";
      const response = await fetch(
        `${API_URL}/notifications/${userId}?page=${page}&limit=10&filter=${filter}&search=${searchQuery}&sort=${sortBy}&archived=${showArchived}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setNotifications(data.notifications);
      setTotalPages(data.totalPages);
      setCurrentPage(Number(data.currentPage));
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setError("Failed to load notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [filter, searchQuery, sortBy, showArchived]);

  // Auto-refresh notifications
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchNotifications(currentPage);
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchNotifications, currentPage]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    if (!notificationId) {
      console.error("Invalid notificationId:", notificationId);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/notifications/${notificationId}/mark-as-read`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
      setError("Failed to mark notification as read. Please try again.");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(
        `${API_URL}/notifications/mark-all-as-read`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) => ({ ...notif, isRead: true }))
      );
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      setError("Failed to mark all notifications as read. Please try again.");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedNotifications.size === 0) return;

    try {
      const response = await fetch(`${API_URL}/notifications/bulk-action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          notificationIds: Array.from(selectedNotifications),
          action: action
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh notifications after bulk action
      fetchNotifications(currentPage);
      setSelectedNotifications(new Set());
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      setError(`Failed to ${action} selected notifications. Please try again.`);
    }
  };

  const handleSelectNotification = (notificationId) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.id)));
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Notifications</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => fetchNotifications(currentPage)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | Notifications</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              <p className="text-sm text-gray-500 mt-1">
                {notifications.filter(n => !n.isRead).length} unread notifications
                {autoRefresh && <span className="ml-2 text-green-600">• Auto-refresh enabled</span>}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </button>
              <button
                onClick={handleMarkAllAsRead}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark All as Read
              </button>
              <button
                onClick={() => fetchNotifications(currentPage)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="mr-2"
                  />
                  Auto-refresh notifications
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Refresh interval (seconds)
                </label>
                <select
                  value={refreshInterval / 1000}
                  onChange={(e) => setRefreshInterval(parseInt(e.target.value) * 1000)}
                  className="border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm"
                >
                  <option value="15">15 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                </select>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="mr-2"
                  />
                  Show archived notifications
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Bulk Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notifications..."
                  className="pl-10 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
              </div>
              
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="all">All Notifications</option>
                <option value="unread">Unread</option>
                <option value="urgent">Urgent</option>
                <option value="high">High Priority</option>
                <option value="job">Job Related</option>
                <option value="application">Applications</option>
                <option value="message">Messages</option>
                <option value="system">System</option>
                <option value="alert">Alerts</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="priority">By Priority</option>
                <option value="type">By Type</option>
              </select>
            </div>

            {/* Bulk Actions */}
            {selectedNotifications.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedNotifications.size} selected
                </span>
                <button
                  onClick={() => handleBulkAction('mark-read')}
                  className="inline-flex items-center px-2 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Mark Read
                </button>
                <button
                  onClick={() => handleBulkAction('archive')}
                  className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  <Archive className="w-4 h-4 mr-1" />
                  Archive
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="inline-flex items-center px-2 py-1 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No Notifications</h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have any notifications at this time.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            {/* Select All Header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedNotifications.size === notifications.length && notifications.length > 0}
                  onChange={handleSelectAll}
                  className="mr-3"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select all notifications
                </span>
              </label>
            </div>

            <ul className="divide-y divide-gray-200">
              {notifications.map((notification) => (
                <li
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                    notification.isRead ? "bg-gray-50" : "bg-white border-l-4 border-l-blue-500"
                  } ${selectedNotifications.has(notification.id) ? "bg-blue-50" : ""}`}
                >
                  <div className="flex items-start space-x-4">
                    <label className="flex-shrink-0 mt-1">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification.id)}
                        onChange={() => handleSelectNotification(notification.id)}
                        className="rounded"
                      />
                    </label>
                    
                    <div className="flex-shrink-0 mt-1">
                      <NotificationIcon 
                        type={notification.type} 
                        priority={notification.priority || 'normal'} 
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                            {notification.message}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-xs text-gray-500">
                              {getTimeAgo(notification.createdAt)}
                            </p>
                            {notification.priority && notification.priority !== 'normal' && (
                              <PriorityBadge priority={notification.priority} />
                            )}
                            <span className="text-xs text-gray-400 capitalize">
                              {notification.type?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          {notification.relatedId && (
                            <button
                              onClick={() => {
                                // Navigate to related item
                                const routes = {
                                  job: `/admin/job-listings`,
                                  application: `/admin/applications`,
                                  blog_draft: `/admin/blog`
                                };
                                const route = routes[notification.type];
                                if (route) window.location.href = route;
                              }}
                              className="p-1 text-gray-400 hover:text-blue-500"
                              title="View related item"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          )}
                          
                          {!notification.isRead ? (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="Mark as read"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Read
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-500">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Read
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={() => fetchNotifications(currentPage - 1)}
              disabled={currentPage === 1}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => fetchNotifications(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
